/**
 * デモデータリセットサービス
 *
 * Supabase Edge Functionsを使用したスナップショット機能:
 * - create-demo-snapshot: デモ開始時のデータバックアップ作成
 * - reset-demo-data: バックアップからデータ復元
 * - cleanup-demo-snapshot: スナップショットのクリーンアップ
 */

import { getSupabaseClient } from '../utils/supabase';
import { AppError, ErrorCategory, ErrorLevel } from '../utils/errorHandler';
import { isDemoMode } from '../utils/environment';
import logger from '../utils/logger';
import EdgeFunctionClient from './core/EdgeFunctionClient';
import {
  DEMO_RESET_INTEGRATION,
  DEMO_DATE_RESET_FUNCTIONS,
  demoDataUtils,
} from '../data/demo/demoDataIntegration';

// =============================================================================
// 型定義
// =============================================================================

export interface DemoSnapshot {
  snapshotId: string;
  timestamp: string;
  tablesBackedUp: string[];
}

export interface DemoSnapshotStats {
  companyId: string;
  snapshotCount: number;
  latestSnapshotDate: string | null;
  totalBackupRecords: number;
  hasSnapshots: boolean;
}

export interface CreateSnapshotResult {
  success: boolean;
  message: string;
  snapshot?: DemoSnapshot;
}

export interface ResetDataResult {
  success: boolean;
  message: string;
  snapshotId: string;
  tablesRestored: string[];
  recordsRestored: Record<string, number>;
}

export interface CleanupResult {
  success: boolean;
  message: string;
  cleanedSnapshots: string[];
  tablesAffected: string[];
  totalRecordsDeleted: number;
}

export interface DemoResetOptions {
  onProgress?: (message: string, step: number, totalSteps: number) => void;
  onError?: (error: AppError) => void;
  createBackup?: boolean;
}

// =============================================================================
// DemoResetService クラス
// =============================================================================

export class DemoResetService {
  private static instance: DemoResetService;
  private currentCompanyId: string | null = null;

  private constructor() {
    // DemoResetServiceのデフォルトコンテキストを設定
    logger.setDefaultContext({
      component: 'DemoResetService',
      file: 'demoResetService.ts',
    });
  }

  public static getInstance(): DemoResetService {
    if (!DemoResetService.instance) {
      DemoResetService.instance = new DemoResetService();
    }
    return DemoResetService.instance;
  }

  /**
   * 会社IDを設定
   */
  public setCompanyId(companyId: string): void {
    this.currentCompanyId = companyId;
  }

  /**
   * 会社IDを取得
   */
  private getCompanyId(): string {
    if (!this.currentCompanyId) {
      throw new AppError(
        '会社IDが設定されていません。ログインし直してください。',
        ErrorCategory.AUTHENTICATION,
        ErrorLevel.HIGH
      );
    }
    return this.currentCompanyId;
  }

  /**
   * デモモードかどうかをチェック
   */
  private validateDemoMode(): void {
    if (!isDemoMode()) {
      throw new AppError(
        'スナップショット機能はデモモードでのみ利用可能です。',
        ErrorCategory.BUSINESS,
        ErrorLevel.HIGH
      );
    }
  }

  /**
   * デモ開始時のスナップショット作成
   */
  public async createSnapshot(options: DemoResetOptions = {}): Promise<CreateSnapshotResult> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      options.onProgress?.('スナップショット作成を開始しています...', 1, 3);

      const operationId = logger.startOperation('createSnapshot', { companyId });

      logger.info(
        'Creating snapshot for company',
        { companyId },
        {
          operationId,
          context: { function: 'createSnapshot' },
          tags: ['snapshot', 'create'],
        }
      );

      const edgeFunctionClient = EdgeFunctionClient;

      const response = await edgeFunctionClient.invoke(
        'create-demo-snapshot',
        { companyId },
        {
          timeout: 60000, // 1分
          retries: 2,
          onProgress: options.onProgress
            ? (message: string) => options.onProgress?.(message, 2, 3)
            : undefined,
        }
      );

      if (!response.success) {
        throw new AppError(
          response.error?.message || 'スナップショット作成が失敗しました',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { metadata: { response } }
        );
      }

      const data = response.data;

      options.onProgress?.('スナップショット作成が完了しました', 3, 3);

      const result: CreateSnapshotResult = {
        success: true,
        message: data?.message || 'スナップショット作成が完了しました',
        snapshot: {
          snapshotId: data?.snapshotId || '',
          timestamp: data?.timestamp || new Date().toISOString(),
          tablesBackedUp: data?.tablesBackedUp || [],
        },
      };

      const duration = Date.now() - parseInt(operationId.split('_')[1]);
      logger.endOperation(operationId, 'createSnapshot', duration, {
        success: true,
        snapshotId: result.snapshot?.snapshotId,
      });

      logger.info(
        'Snapshot created successfully',
        { result },
        {
          operationId,
          context: { function: 'createSnapshot' },
          tags: ['snapshot', 'create', 'success'],
        }
      );
      return result;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `スナップショット作成中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      options.onError?.(appError);

      logger.error(
        'Snapshot creation failed',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        },
        {
          context: { function: 'createSnapshot' },
          tags: ['snapshot', 'create', 'error'],
        }
      );

      return {
        success: false,
        message: appError.message,
      };
    }
  }

  /**
   * デモデータを最新スナップショットから復元
   */
  /**
   * デモデータをテンプレート＋オーバーレイ方式でリセット
   */
  public async resetDemoData(options: DemoResetOptions = {}): Promise<ResetDataResult> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      const totalSteps = 3;
      let currentStep = 1;

      options.onProgress?.(
        'テンプレート＋オーバーレイリセットを開始しています...',
        currentStep++,
        totalSteps
      );

      const operationId = logger.startOperation('resetDemoData', { companyId });

      logger.info(
        'Starting template+overlay demo reset for company',
        { companyId },
        {
          operationId,
          context: { function: 'resetDemoData' },
          tags: ['demo-reset', 'template-overlay', 'start'],
        }
      );

      const edgeFunctionClient = EdgeFunctionClient;

      options.onProgress?.(
        'Edge Functionを呼び出してリセットを実行しています...',
        currentStep++,
        totalSteps
      );

      const response = await edgeFunctionClient.invoke(
        'reset-demo-data',
        {
          reset_reason: 'Manual reset via web interface',
          triggered_by: 'user',
        },
        {
          timeout: 60000, // 1分（テンプレート＋オーバーレイは高速）
          retries: 1,
          onProgress: message => options.onProgress?.(message, 0, 0),
        }
      );

      if (!response.success) {
        throw new AppError(
          response.error?.message || 'データリセットが失敗しました',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { metadata: { response } }
        );
      }

      const data = response.data;

      options.onProgress?.(
        'テンプレート＋オーバーレイリセットが完了しました',
        totalSteps,
        totalSteps
      );

      const result: ResetDataResult = {
        success: true,
        message: data?.message || 'テンプレート＋オーバーレイリセットが完了しました',
        snapshotId: `generation-${data?.newGeneration || 'unknown'}`,
        tablesRestored: [
          'company_cases',
          'company_land_transporters',
          'business_partners',
          'business_partner_locations',
          'deadline_rules',
          'company_partner_selections',
        ],
        recordsRestored: {
          generation_reset: data?.newGeneration || 0,
          overlay_tables: Object.keys(data?.overlayStatistics || {}).length,
        },
      };

      const duration = Date.now() - parseInt(operationId.split('_')[1]);
      logger.endOperation(operationId, 'resetDemoData', duration, {
        success: true,
        generation: data?.newGeneration,
        overlayTables: Object.keys(data?.overlayStatistics || {}).length,
      });

      logger.info(
        'Template+overlay demo reset completed successfully',
        {
          result: {
            generation: `${data?.previousGeneration} → ${data?.newGeneration}`,
            overlayStatistics: data?.overlayStatistics,
          },
        },
        {
          operationId,
          context: { function: 'resetDemoData' },
          tags: ['demo-reset', 'template-overlay', 'success'],
        }
      );
      return result;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `テンプレート＋オーバーレイリセット中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      options.onError?.(appError);

      logger.error(
        'Template+overlay demo reset failed',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        },
        {
          context: { function: 'resetDemoData' },
          tags: ['demo-reset', 'template-overlay', 'error'],
        }
      );

      return {
        success: false,
        message: appError.message,
        snapshotId: '',
        tablesRestored: [],
        recordsRestored: {},
      };
    }
  }

  /**
   * 重複バックアップデータのクリーンアップ
   */
  public async cleanupDuplicateBackups(dryRun: boolean = true): Promise<any> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      logger.info('[DemoResetService] Cleaning up duplicate backup data for company:', {
        companyId,
      });

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new AppError(
          'Supabaseクライアントが初期化されていません',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      const { data, error } = await supabase.functions.invoke('cleanup-duplicate-backup', {
        body: { companyId, dryRun },
      });

      if (error) {
        throw new AppError(
          `重複データクリーンアップに失敗しました: ${error.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          error
        );
      }

      console.log('[DemoResetService] Duplicate backup cleanup completed:', data);
      return data;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `重複データクリーンアップ中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      console.error('[DemoResetService] Duplicate backup cleanup failed:', error);
      throw appError;
    }
  }

  /**
   * スナップショットのクリーンアップ
   */
  public async cleanupSnapshots(olderThanHours?: number): Promise<CleanupResult> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      console.log('[DemoResetService] Cleaning up snapshots for company:', companyId);

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new AppError(
          'Supabaseクライアントが初期化されていません',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      const body: any = { companyId };
      if (olderThanHours !== undefined) {
        body.olderThanHours = olderThanHours;
      }

      const { data, error } = await supabase.functions.invoke('cleanup-demo-snapshot', {
        body,
      });

      if (error) {
        throw new AppError(
          `スナップショットクリーンアップに失敗しました: ${error.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          error
        );
      }

      if (!data?.success) {
        throw new AppError(
          data?.message || 'スナップショットクリーンアップが失敗しました',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      const result: CleanupResult = {
        success: true,
        message: data?.message || 'クリーンアップが完了しました',
        cleanedSnapshots: data?.cleanedSnapshots || 0,
        tablesAffected: data?.tablesAffected || [],
        totalRecordsDeleted: data?.totalRecordsDeleted || 0,
      };

      console.log('[DemoResetService] Cleanup completed:', result);
      return result;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `スナップショットクリーンアップ中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      console.error('[DemoResetService] Cleanup failed:', error);

      return {
        success: false,
        message: appError.message,
        cleanedSnapshots: [],
        tablesAffected: [],
        totalRecordsDeleted: 0,
      };
    }
  }

  /**
   * スナップショット統計情報を取得
   */
  public async getSnapshotStats(): Promise<DemoSnapshotStats | null> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      console.log('[DemoResetService] Getting snapshot stats for company:', companyId);

      const supabase = getSupabaseClient();
      if (!supabase) {
        console.warn('[DemoResetService] Supabaseクライアントが初期化されていません');
        return null;
      }

      const { data, error } = await supabase.rpc('get_demo_snapshot_stats', {
        p_company_id: companyId,
      });

      if (error) {
        console.warn('[DemoResetService] Failed to get snapshot stats:', error);
        return null;
      }

      return data as DemoSnapshotStats;
    } catch (error) {
      console.warn('[DemoResetService] Error getting snapshot stats:', error);
      return null;
    }
  }

  /**
   * テンプレート＋オーバーレイ方式での「バックアップ」確認
   *
   * 新方式の動作メカニズム:
   * - 「テンプレートデータ」は、初期状態のデモデータを格納する専用テーブル（例: company_cases_template）に保存され、常に保護されています。
   * - 「オーバーレイデータ」は、実際のデモ利用中に変更・追加されるデータで、通常の業務テーブルに格納されます。
   * - リセット時は、オーバーレイデータを削除または上書きし、テンプレートデータを基に業務テーブルを再構築することで、初期状態に戻します。
   * - この方式により、個別のバックアップ世代を持たずとも、テンプレートデータが存在する限り、いつでもリセットが可能です。
   * - 本メソッドでは、テンプレートテーブルの存在と現在の世代番号を確認し、リセット可能性を検証します。
   */
  public async verifyBackupExists(): Promise<{
    hasBackup: boolean;
    backupInfo?: any;
    error?: string;
  }> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      console.log(
        '[DemoResetService] Verifying template+overlay reset capability for company:',
        companyId
      );

      const supabase = getSupabaseClient();
      if (!supabase) {
        return { hasBackup: false, error: 'Supabaseクライアントが初期化されていません' };
      }

      // 現在の世代番号を確認
      const { data: currentGeneration, error: generationError } =
        await supabase.rpc('current_demo_generation');

      if (generationError) {
        console.warn('[DemoResetService] Failed to get current generation:', generationError);
        return { hasBackup: false, error: `世代番号取得エラー: ${generationError.message}` };
      }

      // テンプレートテーブルの存在確認（一例としてcompany_cases_template）
      const { data: templateCheck, error: templateError } = await supabase
        .from('company_cases_template')
        .select('id')
        .limit(1);

      if (templateError) {
        console.warn('[DemoResetService] Template table check failed:', templateError);
        return {
          hasBackup: false,
          error: `テンプレートテーブル確認エラー: ${templateError.message}`,
        };
      }

      return {
        hasBackup: true,
        backupInfo: {
          resetType: 'template_overlay',
          currentGeneration: currentGeneration || 1,
          templateTablesVerified: true,
          templateRecordsSample: templateCheck?.length || 0,
          resetMethod: 'generation_increment',
        },
      };
    } catch (error) {
      console.error('[DemoResetService] Error verifying template+overlay capability:', error);
      return {
        hasBackup: false,
        error: `リセット可能性確認中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
      };
    }
  }

  /**
   * スナップショット作成後に追加されたカスタムデータのみを取得
   */
  /**
   * テンプレート＋オーバーレイ方式での現在のオーバーレイデータ取得
   * 新方式では、現在の世代のオーバーレイデータがリセット対象となる
   */
  public async getCustomDataAddedAfterSnapshot(): Promise<{
    customPartners: any[];
    snapshotInfo?: any;
    error?: string;
  }> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      console.log(
        '[DemoResetService] Getting current overlay data for template+overlay reset, company:',
        companyId
      );

      const supabase = getSupabaseClient();
      if (!supabase) {
        return { customPartners: [], error: 'Supabaseクライアントが初期化されていません' };
      }

      // 現在の世代番号を取得
      const { data: currentGeneration, error: generationError } =
        await supabase.rpc('current_demo_generation');

      if (generationError) {
        console.warn('[DemoResetService] Failed to get current generation:', generationError);
        return { customPartners: [], error: `世代番号取得エラー: ${generationError.message}` };
      }

      console.log('[DemoResetService] Current generation:', currentGeneration);

      // 現在の世代のオーバーレイデータを取得（business_partners_overlayから）
      const { data: overlayPartners, error: overlayError } = await supabase
        .from('business_partners_overlay')
        .select('id, company_id, name, category, type, operation_type, overlay_created_at')
        .eq('company_id', companyId)
        .eq('generation_number', currentGeneration || 1)
        .order('overlay_created_at', { ascending: false });

      if (overlayError) {
        console.warn('[DemoResetService] Failed to get overlay data:', overlayError);
        return {
          customPartners: [],
          error: `オーバーレイデータ取得エラー: ${overlayError.message}`,
        };
      }

      console.log(
        '[DemoResetService] Found overlay partners:',
        (overlayPartners || []).length,
        'records in generation',
        currentGeneration
      );

      // CREATEとUPDATEオペレーションのデータのみを表示対象とする（DELETEは除外）
      const activeOverlayData = (overlayPartners || []).filter(
        partner => partner.operation_type !== 'DELETE'
      );

      console.log(
        '[DemoResetService] Active overlay data (excluding DELETEs):',
        activeOverlayData.length,
        'records'
      );

      return {
        customPartners: activeOverlayData.map(partner => ({
          id: partner.id,
          name: partner.name,
          category: partner.category,
          type: partner.type,
          status: 'active', // オーバーレイデータは基本的にアクティブ
          created_at: partner.overlay_created_at,
          operation_type: partner.operation_type,
        })),
        snapshotInfo: {
          resetType: 'template_overlay',
          currentGeneration: currentGeneration || 1,
          nextGeneration: (currentGeneration || 1) + 1,
          overlayRecordsFound: activeOverlayData.length,
          description:
            'テンプレート＋オーバーレイ方式により、世代をインクリメントしてリセットします',
        },
      };
    } catch (error) {
      console.error('[DemoResetService] Error getting overlay data:', error);
      return {
        customPartners: [],
        error: `オーバーレイデータ取得中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
      };
    }
  }

  /**
   * 現在のカスタムデータの確認（全件取得）
   * @deprecated デモリセット用途では getCustomDataAddedAfterSnapshot() を使用してください
   */
  public async getCurrentCustomData(): Promise<{ customPartners: any[]; error?: string }> {
    this.validateDemoMode();
    const companyId = this.getCompanyId();

    try {
      console.log('[DemoResetService] Getting current custom data for company:', companyId);

      const supabase = getSupabaseClient();
      if (!supabase) {
        return { customPartners: [], error: 'Supabaseクライアントが初期化されていません' };
      }

      const { data: customPartners, error } = await supabase
        .from('business_partners')
        .select('id, name, category, type, status, created_at')
        .eq('company_id', companyId)
        .eq('data_source', 'custom')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('[DemoResetService] Failed to get custom data:', error);
        return { customPartners: [], error: `カスタムデータ取得エラー: ${error.message}` };
      }

      console.log(
        '[DemoResetService] getCurrentCustomData result:',
        (customPartners || []).map(p => ({ id: p.id, name: p.name, created_at: p.created_at }))
      );

      return { customPartners: customPartners || [] };
    } catch (error) {
      console.error('[DemoResetService] Error getting custom data:', error);
      return {
        customPartners: [],
        error: `カスタムデータ取得中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
      };
    }
  }

  /**
   * 安全なデモリセット（事前チェック付き）
   */
  public async safeResetDemoData(options: DemoResetOptions = {}): Promise<ResetDataResult> {
    this.validateDemoMode();
    // const companyId = this.getCompanyId(); // companyIdは使用されていないがvalidationのために必要

    try {
      const totalSteps = 7;
      let currentStep = 1;

      // Step 1: バックアップ存在確認
      options.onProgress?.('バックアップの存在を確認しています...', currentStep++, totalSteps);
      const backupCheck = await this.verifyBackupExists();

      if (!backupCheck.hasBackup) {
        throw new AppError(
          `リセットを安全に実行できません: ${backupCheck.error}`,
          ErrorCategory.BUSINESS,
          ErrorLevel.HIGH
        );
      }

      console.log('[DemoResetService] Backup verification passed:', backupCheck.backupInfo);

      // Step 2: 現在のカスタムデータ確認
      options.onProgress?.('現在のカスタムデータを確認しています...', currentStep++, totalSteps);
      const customDataCheck = await this.getCurrentCustomData();

      if (customDataCheck.error) {
        console.warn('[DemoResetService] Custom data check warning:', customDataCheck.error);
      }

      const hasCustomData = customDataCheck.customPartners.length > 0;
      console.log(
        '[DemoResetService] Current custom data count:',
        customDataCheck.customPartners.length
      );

      // Step 3: 追加のバックアップ作成（カスタムデータがある場合）
      // 注意: 既存のスナップショットがあるため、追加バックアップは通常不要
      // オプションで明示的に要求された場合のみ作成
      if (hasCustomData && options.createBackup === true) {
        console.log(
          '[DemoResetService] Explicit backup requested, creating additional snapshot...'
        );
        options.onProgress?.(
          '現在のカスタムデータをバックアップしています...',
          currentStep++,
          totalSteps
        );
        const additionalBackup = await this.createSnapshot({ onProgress: options.onProgress });

        if (!additionalBackup.success) {
          console.warn(
            '[DemoResetService] Additional backup failed, but continuing with existing backup'
          );
        } else {
          console.log(
            '[DemoResetService] Additional backup created:',
            additionalBackup.snapshot?.snapshotId
          );
        }
      } else {
        console.log('[DemoResetService] Skipping additional backup - using existing snapshot');
        currentStep++; // Skip this step
      }

      // Step 4: データリセット実行
      options.onProgress?.('データリセットを実行しています...', currentStep++, totalSteps);
      const resetResult = await this.resetDemoData({
        onProgress: options.onProgress,
        createBackup: false, // 既に事前チェック済み
      });

      if (!resetResult.success) {
        throw new AppError(
          `データリセットに失敗しました: ${resetResult.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      // Step 5: リセット後の確認
      options.onProgress?.('リセット結果を確認しています...', currentStep++, totalSteps);
      const afterResetCheck = await this.getCurrentCustomData();

      // Step 6: 結果の検証
      options.onProgress?.('リセット完了を検証しています...', currentStep++, totalSteps);
      const customDataRemaining = afterResetCheck.customPartners.length;

      if (customDataRemaining > 0) {
        console.warn(
          '[DemoResetService] Warning: Custom data still remains after reset:',
          customDataRemaining
        );
      }

      options.onProgress?.('安全なリセットが完了しました', totalSteps, totalSteps);

      const result: ResetDataResult = {
        ...resetResult,
        message: `${resetResult.message} (カスタムデータ削除前: ${customDataCheck.customPartners.length}件, 削除後: ${customDataRemaining}件)`,
      };

      console.log('[DemoResetService] Safe reset completed:', result);
      return result;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `安全なリセット中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      options.onError?.(appError);
      console.error('[DemoResetService] Safe reset failed:', error);

      return {
        success: false,
        message: appError.message,
        snapshotId: '',
        tablesRestored: [],
        recordsRestored: {},
      };
    }
  }

  /**
   * デモ体験の完全リセット（スナップショット作成 + データリセット）
   */
  public async fullDemoReset(options: DemoResetOptions = {}): Promise<ResetDataResult> {
    this.validateDemoMode();

    try {
      options.onProgress?.('デモ体験の完全リセットを開始しています...', 1, 6);

      // 1. 現在のデータでスナップショット作成
      options.onProgress?.('現在のデータのスナップショットを作成しています...', 2, 6);
      const snapshotResult = await this.createSnapshot({ onProgress: options.onProgress });

      if (!snapshotResult.success) {
        throw new AppError(
          'スナップショット作成に失敗したため、リセットを中止します',
          ErrorCategory.BUSINESS,
          ErrorLevel.HIGH
        );
      }

      // 2. 安全なデータリセット実行
      options.onProgress?.('安全なデータリセットを実行しています...', 4, 6);
      const resetResult = await this.safeResetDemoData({
        onProgress: options.onProgress,
        createBackup: false, // 既にスナップショットを作成済み
      });

      options.onProgress?.('完全リセットが完了しました', 6, 6);

      return resetResult;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `完全リセット中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      options.onError?.(appError);
      console.error('[DemoResetService] Full demo reset failed:', error);

      return {
        success: false,
        message: appError.message,
        snapshotId: '',
        tablesRestored: [],
        recordsRestored: {},
      };
    }
  }

  /**
   * FL-132: 日付固定デモリセット（2025年7月11日固定）
   */
  public async resetWithFixedDate(options: DemoResetOptions = {}): Promise<
    ResetDataResult & {
      dateFixed: boolean;
      demoDataValidated: boolean;
    }
  > {
    this.validateDemoMode();

    try {
      // FL-132統合リセット実行
      const integrationResult = await DEMO_RESET_INTEGRATION.executeFullDemoReset(
        this,
        options.onProgress
      );

      if (!integrationResult.success) {
        throw new AppError(
          `FL-132統合リセットに失敗しました: ${integrationResult.message}`,
          ErrorCategory.BUSINESS,
          ErrorLevel.HIGH
        );
      }

      // データ検証実行
      const validation = DEMO_RESET_INTEGRATION.validateDemoData();

      if (!validation.isValid) {
        logger.warn(
          'Demo data validation warnings',
          { issues: validation.issues },
          {
            context: { function: 'resetWithFixedDate' },
            tags: ['demo-reset', 'validation', 'warning'],
          }
        );
      }

      const result: ResetDataResult & {
        dateFixed: boolean;
        demoDataValidated: boolean;
      } = {
        success: true,
        message: `FL-132デモリセットが完了しました。${integrationResult.message}`,
        snapshotId: 'fl-132-demo-reset',
        tablesRestored: ['business_partners', 'cases', 'land_transporters', 'schedules'],
        recordsRestored: {
          business_partners: integrationResult.details.partnerCount,
          cases: integrationResult.details.caseCount,
          transporters: integrationResult.details.transporterCount,
          schedules: 7, // 週間スケジュール
        },
        dateFixed: integrationResult.details.dateFixed,
        demoDataValidated: validation.isValid,
      };

      logger.info(
        'FL-132 demo reset completed successfully',
        {
          result: {
            dateFixed: result.dateFixed,
            dataValidated: result.demoDataValidated,
            validation: validation.summary,
          },
        },
        {
          context: { function: 'resetWithFixedDate' },
          tags: ['demo-reset', 'fl-132', 'success'],
        }
      );

      return result;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError(
              `FL-132デモリセット中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: error instanceof Error ? error : undefined }
            );

      options.onError?.(appError);

      logger.error(
        'FL-132 demo reset failed',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        },
        {
          context: { function: 'resetWithFixedDate' },
          tags: ['demo-reset', 'fl-132', 'error'],
        }
      );

      return {
        success: false,
        message: appError.message,
        snapshotId: '',
        tablesRestored: [],
        recordsRestored: {},
        dateFixed: false,
        demoDataValidated: false,
      };
    }
  }

  /**
   * FL-132: デモ体験の進捗確認
   */
  public getDemoProgress(completedPhases: string[] = []): {
    progress: any;
    recommendedFlow: any[];
    validation: any;
  } {
    this.validateDemoMode();

    const progress = demoDataUtils.calculateDemoProgress(completedPhases);
    const recommendedFlow = DEMO_RESET_INTEGRATION.getRecommendedDemoFlow();
    const validation = DEMO_RESET_INTEGRATION.validateDemoData();

    return {
      progress,
      recommendedFlow,
      validation,
    };
  }

  /**
   * FL-132: デモ日付の管理
   */
  public manageDemoDate() {
    this.validateDemoMode();

    return {
      setFixedDate: DEMO_DATE_RESET_FUNCTIONS.setDemoDateMode.bind(DEMO_DATE_RESET_FUNCTIONS),
      clearFixedDate: DEMO_DATE_RESET_FUNCTIONS.clearDemoDateMode.bind(DEMO_DATE_RESET_FUNCTIONS),
      getFixedDate: DEMO_DATE_RESET_FUNCTIONS.getFixedDate.bind(DEMO_DATE_RESET_FUNCTIONS),
      isDemoModeActive: DEMO_DATE_RESET_FUNCTIONS.isDemoModeActive.bind(DEMO_DATE_RESET_FUNCTIONS),
      getDemoCurrentDate:
        DEMO_DATE_RESET_FUNCTIONS.getDemoCurrentDate.bind(DEMO_DATE_RESET_FUNCTIONS),
    };
  }
}

// エクスポート用の便利関数
export const demoResetService = DemoResetService.getInstance();

// デフォルトエクスポート
export default DemoResetService;
